import React, { useEffect, useState } from 'react';
import { Button, Divider, Select, Space, Tag, TagProps } from 'antd';
import { TablePaginationConfig, TableProps } from 'antd/lib/table';
import qs from 'query-string';

import history from 'src/libs/history';
import useQueryUrl from 'src/hooks//useQueryUrl';
import { t } from 'src/libs/i18n';
import { optionSelect } from 'src/utils/helper';
import { PER } from 'src/constants/app';

type UseTable = {
  hasSelect?: boolean;
  pagination?: any;
  maxPageSize?: 100 | 200 | 500 | 1500;
  perDefault?: number;
};

const useTable = <T extends { seqno: string | number }>({
  hasSelect = true,
  maxPageSize = 200,
  perDefault = PER.DEFAULT,
}: UseTable) => {
  const query = useQueryUrl();
  const [rowKeysSelected, setRowKeysSelected] = useState<React.Key[]>([]);
  const defaultPageSize = query.offset ? Number(query.offset) : perDefault;
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    setPageSize(query.offset ? Number(query.offset) : perDefault);
  }, [query.offset]);

  const onChangePage = (page: number) => {
    delete query.search;
    history.push({
      search: qs.stringify(
        { ...query, page },
        {
          skipNull: true,
          skipEmptyString: true,
          arrayFormatSeparator: '|',
          arrayFormat: 'bracket-separator',
        },
      ),
    });
  };

  const onChangePageSize = (offset) => {
    delete query.search;
    history.push({
      search: qs.stringify(
        { ...query, offset, page: 1 },
        {
          skipNull: true,
          skipEmptyString: true,
          arrayFormatSeparator: '|',
          arrayFormat: 'bracket-separator',
        },
      ),
    });
  };

  const paginationProps: TablePaginationConfig = {
    position: ['topRight', 'bottomRight'],
    onChange: onChangePage,
    pageSize: defaultPageSize,
    showSizeChanger: false,
    showTotal(total) {
      return (
        <Space className="show_total_space_container">
          <span>{t('table.page_size')}</span>
          <Select
            size="small"
            value={pageSize}
            onChange={setPageSize}
            options={optionSelect(maxPageSize)}
          />
          <span>{t('table.record')}</span>
          <Button
            type="primary"
            size="small"
            onClick={() => onChangePageSize(pageSize)}
          >
            {t('table.apply_page_size')}
          </Button>
          <Divider type="vertical" />
          <span>
            {t('table.show_items', { total: total.toLocaleString() })}
          </span>
          <Divider type="vertical" />
        </Space>
      );
    },
  };

  const rowSelection: TableProps<T>['rowSelection'] = {
    type: 'checkbox',
    onSelect(data, checked) {
      if (checked) {
        setRowKeysSelected([...rowKeysSelected, data.seqno]);
        return;
      }

      const rowFiltered = rowKeysSelected.filter((row) => row !== data.seqno);
      setRowKeysSelected(rowFiltered);
    },
    onSelectAll(checked, changed, selected) {
      const listIds = selected.map((d) => d.seqno) as React.Key[];
      if (checked) {
        setRowKeysSelected([...rowKeysSelected, ...listIds]);
        return;
      }

      const rowFiltered = rowKeysSelected.filter(
        (key) => !listIds.includes(key),
      );
      setRowKeysSelected(rowFiltered);
    },
    selectedRowKeys: rowKeysSelected,
  };

  const Selected = (props: TagProps) => {
    if (rowKeysSelected.length) {
      return React.createElement(
        Tag,
        {
          closable: true,
          onClose: () => setRowKeysSelected([]),
          color: 'orange',
          style: {
            fontSize: 14,
            padding: '4px 8px',
          },
          ...props,
        },
        props.children,
      );
    }

    return null;
  };

  return {
    pagination: paginationProps,
    rowSelection: hasSelect ? rowSelection : undefined,
    Selected,
    rowSelected: rowKeysSelected.map((key) => Number(key)),
    setRowKeysSelected,
    setPageSize,
    pageSize,
  };
};

export default useTable;
